import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { Control, Controller } from 'react-hook-form';
import { ErrorComponent } from './ErrorComponent';
import { defaultDatePickerStrings, DayOfWeek } from '@fluentui/react';
import {
  DateContainer,
  DatePickerContainer,
  DatePickerStyle,
  HeaderContainer,
  HeaderLabel,
} from './styles/DateController.styled';
import { ErrorrContainer } from './styles/ErrorComponent.styled';
import { getWeekendsOfYear } from 'src/utils/dateFns/getWeekendsOfMonth';
import { getNonWorkingDaysOfYear } from 'src/utils/dateFns/findNonWorkingDaysOfMonth';
import { EmployeeVacationDays } from '../../../../tabs/models/SummaryInterfaces';
import {
  MAX_DATE_DAY_VALUE,
  MAX_DATE_MONTH_VALUE,
} from 'src/tabs/constants/Timeoff.constants';
import { IForm } from '../NewRequestPopUp';

interface DateControllerProps {
  control: Control<IForm>;
  startDate?: Date;
  setStartDate?: Dispatch<SetStateAction<Date>>;
  dateLabel: string;
  controllerName: keyof IForm;
  holidays: Array<string>;
  vacationPeriodNextYear: EmployeeVacationDays;
  selectedYear: number;
  disabled?: boolean;
}

export const DateController = ({
  control,
  setStartDate,
  startDate,
  dateLabel,
  controllerName,
  holidays,
  vacationPeriodNextYear,
  selectedYear,
  disabled,
}: DateControllerProps) => {
  const dateYear = new Date().getFullYear();
  const [initialDate, setInitialDate] = useState(new Date());
  const weekends = getWeekendsOfYear(dateYear);
  const weekendsYearNext = getWeekendsOfYear(dateYear + 1);
  const holidaysDateArray = getNonWorkingDaysOfYear(dateYear, holidays);
  const restrictedDates = [
    ...weekends,
    ...weekendsYearNext,
    ...holidaysDateArray,
  ];

  useEffect(() => {
    if (controllerName === 'endDate' && startDate) {
      setInitialDate(startDate);
    }
  }, [controllerName, startDate]);

  const onSelectDateHandler = (
    date: Date,
    onChange: (...event: Date[]) => void
  ) => {
    if (date && controllerName === 'endDate') {
      onChange(date);
    } else {
      onChange(date);
      setStartDate(date);
    }
  };

  return (
    <Controller
      name={controllerName}
      control={control}
      render={({ field: { onChange, onBlur }, fieldState: { error } }) => (
        <DateContainer>
          <HeaderContainer>
            <HeaderLabel htmlFor={controllerName}>
              {dateLabel} <span className="required">*</span>
            </HeaderLabel>
          </HeaderContainer>
          <DatePickerContainer>
            <DatePickerStyle
              id={controllerName}
              placeholder="dd/mm/yyyy"
              ariaLabel="Select a date"
              onSelectDate={(date) => onSelectDateHandler(date, onChange)}
              strings={defaultDatePickerStrings}
              onBlur={onBlur}
              minDate={startDate ? startDate : new Date()}
              error={error}
              maxDate={
                !vacationPeriodNextYear &&
                new Date(selectedYear, MAX_DATE_MONTH_VALUE, MAX_DATE_DAY_VALUE)
              }
              firstDayOfWeek={DayOfWeek.Monday}
              today={new Date(1900, 1, 1)}
              showGoToToday={false}
              calendarProps={{ restrictedDates }}
              isMonthPickerVisible={false}
              initialPickerDate={initialDate}
              disabled={disabled}
            />
            <ErrorrContainer>
              <ErrorComponent error={error} />
            </ErrorrContainer>
          </DatePickerContainer>
        </DateContainer>
      )}
    />
  );
};
